/**
 * API client for BidBandit backend.
 */

import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
    baseURL: `${API_BASE_URL}/v1`,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
            const value = params[key];
            if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(key, v));
            } else if (value !== undefined && value !== null) {
                searchParams.append(key, value);
            }
        }
        return searchParams.toString();
    },
});

// Add auth token to requests
apiClient.interceptors.request.use(async (config) => {
    // Clerk provides the session token
    // @ts-ignore - Clerk types
    const token = await window.Clerk?.session?.getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Search API
export const searchApi = {
    search: async (query: string, filters?: Record<string, any>) => {
        const params = new URLSearchParams({ q: query, ...filters });
        const response = await apiClient.get(`/search?${params}`);
        return response.data;
    },
};

// Projects API
export const projectsApi = {
    list: async (params?: { page?: number; pageSize?: number; agency?: string; status?: string; opportunity_type?: string }) => {
        const response = await apiClient.get('/projects', { params });
        return response.data;
    },
    get: async (id: number) => {
        const response = await apiClient.get(`/projects/${id}`);
        return response.data;
    },
    getBidItems: async (id: number, params?: { page?: number; pageSize?: number }) => {
        const response = await apiClient.get(`/projects/${id}/bid_items`, { params });
        return response.data;
    },
    create: async (data: any) => {
        const response = await apiClient.post('/projects', data);
        return response.data;
    },
    delete: async (id: number) => {
        await apiClient.delete(`/projects/${id}`);
    },
};

// Documents API
export const documentsApi = {
    list: async (params?: { projectId?: number; page?: number }) => {
        const response = await apiClient.get('/documents', { params });
        return response.data;
    },
    upload: async (file: File, metadata: { projectId?: number; tags?: string[] }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (metadata.projectId) formData.append('project_id', String(metadata.projectId));
        if (metadata.tags) formData.append('tags', metadata.tags.join(','));

        const response = await apiClient.post('/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

// Ingestion API (Deprecated, see importedApi)
export const ingestionApi = {
    listBatches: async (params?: { status?: string; page?: number }) => {
        // Redirect to imported if needed or keep as stub
        const response = await apiClient.get('/imported', { params });
        return response.data;
    },
    // ... other methods if needed
};

// Agencies API
export const agenciesApi = {
    list: async () => {
        const response = await apiClient.get('/agencies');
        return response.data;
    },
    run: async (id: number) => {
        const response = await apiClient.post(`/agencies/${id}/run`);
        return response.data;
    },
};

// Feeds API (Replaces Sources)
export const feedsApi = {
    list: async (params?: { agency_id?: number }) => {
        const response = await apiClient.get('/feeds', { params });
        return response.data;
    },
    run: async (id: number) => {
        const response = await apiClient.post(`/feeds/${id}/run`);
        return response.data;
    },
    toggle: async (id: number) => {
        const response = await apiClient.post(`/feeds/${id}/toggle`);
        return response.data;
    },
};

// Imported Items API
export const importedApi = {
    list: async (params: {
        type: string;
        status?: string;
        page?: number;
        limit?: number;
        search?: string;
        agency_ids?: number[];
        min_price?: number;
        max_price?: number;
        date_from?: string;
        date_to?: string;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
    }) => {
        const response = await apiClient.get('/imported', { params });
        return response.data;
    },
    move: async (id: number, data: { notes?: string } = {}) => {
        const response = await apiClient.post(`/imported/${id}/move`, data);
        return response.data;
    },
    delete: async (id: number) => {
        await apiClient.delete(`/imported/${id}`);
    },
    bulkDelete: async (ids: number[]) => {
        await apiClient.post('/imported/bulk-delete', { ids });
    },
    bulkMove: async (ids: number[], data: { notes?: string } = {}) => {
        const response = await apiClient.post('/imported/bulk-move', { ids, ...data });
        return response.data;
    },
};

// Pursuits API
export const pursuitsApi = {
    getDashboardKanban: async () => {
        const response = await apiClient.get('/pursuits/dashboard/kanban');
        return response.data;
    },
    list: async (params?: { stage?: string }) => {
        const response = await apiClient.get('/pursuits', { params });
        return response.data;
    },
    get: async (id: number) => {
        const response = await apiClient.get(`/pursuits/${id}`);
        return response.data;
    },
    update: async (id: number, data: any) => {
        const response = await apiClient.patch(`/pursuits/${id}`, data);
        return response.data;
    },
    return: async (id: number) => {
        const response = await apiClient.post(`/pursuits/${id}/return`);
        return response.data;
    },
    delete: async (id: number) => {
        await apiClient.delete(`/pursuits/${id}`);
    },
};

// Price Sets API
export const priceSetsApi = {
    list: async () => {
        const response = await apiClient.get('/price-sets');
        return response.data;
    },
    get: async (id: number) => {
        const response = await apiClient.get(`/price-sets/${id}`);
        return response.data;
    },
    create: async (data: { name: string; description?: string; category?: string; initial_items?: any[] }) => {
        const response = await apiClient.post('/price-sets', data);
        return response.data;
    },
    update: async (id: number, data: { name?: string; description?: string; category?: string; is_shared?: boolean }) => {
        const response = await apiClient.patch(`/price-sets/${id}`, data);
        return response.data;
    },
    delete: async (id: number) => {
        await apiClient.delete(`/price-sets/${id}`);
    },
    duplicate: async (id: number) => {
        const response = await apiClient.post(`/price-sets/${id}/duplicate`);
        return response.data;
    },
    // Line items
    addLineItem: async (setId: number, data: any) => {
        const response = await apiClient.post(`/price-sets/${setId}/line-items`, data);
        return response.data;
    },
    updateLineItem: async (setId: number, itemId: number, data: any) => {
        const response = await apiClient.patch(`/price-sets/${setId}/line-items/${itemId}`, data);
        return response.data;
    },
    deleteLineItem: async (setId: number, itemId: number) => {
        await apiClient.delete(`/price-sets/${setId}/line-items/${itemId}`);
    },
    reorderLineItems: async (setId: number, orderedIds: number[]) => {
        const response = await apiClient.post(`/price-sets/${setId}/line-items/reorder`, { ordered_ids: orderedIds });
        return response.data;
    },
    // Export
    export: async (setId: number, format: 'csv' | 'xlsx' = 'csv') => {
        const response = await apiClient.get(`/price-sets/${setId}/export`, {
            params: { format },
            responseType: 'blob',
        });
        return response.data;
    },
    // Market intel
    marketIntel: async (setId: number) => {
        const response = await apiClient.get(`/price-sets/${setId}/market-intel`);
        return response.data;
    },
};

// Sources API (Legacy)
export const sourcesApi = {
    list: async () => {
        const response = await apiClient.get('/feeds');
        return response.data;
    },
    // Add other stubs if needed for backward compatibility
};

// Auth API
export const authApi = {
    me: async () => {
        const response = await apiClient.get('/auth/me');
        return response.data;
    },
};

// AI Analysis API
export const aiApi = {
    summarize: async (projectId: number) => {
        const response = await apiClient.post('/ai/summarize', { project_id: projectId });
        return response.data;
    },
    getCompliance: async (projectId: number) => {
        const response = await apiClient.post(`/ai/compliance/${projectId}`);
        return response.data;
    },
    analyzeWinFactors: async (projectId: number) => {
        const response = await apiClient.post(`/ai/win-factors/${projectId}`);
        return response.data;
    },
    getPriceTrends: async () => {
        const response = await apiClient.get('/ai/price-trends');
        return response.data;
    },
    getPipelineStats: async () => {
        const response = await apiClient.get('/ai/pipeline-stats');
        return response.data;
    },
    chat: async (message: string, projectId?: number, sessionId?: number, contextText?: string) => {
        const response = await apiClient.post('/ai/chat', {
            message,
            project_id: projectId,
            session_id: sessionId,
            context_text: contextText
        });
        return response.data;
    },
    chatStream: async (
        message: string,
        projectId: number | undefined,
        sessionId: number | undefined,
        onChunk: (text: string) => void,
    ): Promise<void> => {
        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        // @ts-ignore
        const token = await window.Clerk?.session?.getToken();
        const res = await fetch(`${baseUrl}/v1/ai/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ message, project_id: projectId, session_id: sessionId }),
        });
        if (!res.ok) throw new Error(`Stream failed: ${res.status}`);
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let reading = true;
        while (reading) {
            const { done, value } = await reader.read();
            if (done) {
                reading = false;
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                try {
                    const data = JSON.parse(line.slice(6));
                    if (data.text) onChunk(data.text);
                } catch { /* skip malformed */ }
            }
        }
    },
    getChatHistory: async (params: { project_id?: number; imported_item_id?: number; session_id?: number }) => {
        const response = await apiClient.get('/ai/chat/history', { params });
        return response.data;
    },
    getChatSessions: async (params: { project_id?: number; imported_item_id?: number }) => {
        const response = await apiClient.get('/ai/chat/sessions', { params });
        return response.data;
    },
    createChatSession: async (data: { title?: string; project_id?: number; imported_item_id?: number }) => {
        const response = await apiClient.post('/ai/chat/sessions', data);
        return response.data;
    },
    renameChatSession: async (sessionId: number, title: string) => {
        const response = await apiClient.patch(`/ai/chat/sessions/${sessionId}`, { title });
        return response.data;
    },
    deleteChatSession: async (sessionId: number) => {
        await apiClient.delete(`/ai/chat/sessions/${sessionId}`);
    },

    // Global AI Agent
    globalChatStream: async (
        message: string,
        sessionId: number | undefined,
        onChunk: (text: string) => void,
    ): Promise<void> => {
        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        // @ts-ignore
        const token = await window.Clerk?.session?.getToken();
        const res = await fetch(`${baseUrl}/v1/ai/chat/global/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ message, session_id: sessionId }),
        });
        if (!res.ok) throw new Error(`Stream failed: ${res.status}`);
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let reading = true;
        while (reading) {
            const { done, value } = await reader.read();
            if (done) {
                reading = false;
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                try {
                    const data = JSON.parse(line.slice(6));
                    if (data.text) onChunk(data.text);
                } catch { /* skip malformed */ }
            }
        }
    },
    getGlobalSessions: async () => {
        const response = await apiClient.get('/ai/chat/sessions/global');
        return response.data;
    },
    createGlobalSession: async (data: { title?: string }) => {
        const response = await apiClient.post('/ai/chat/sessions/global', data);
        return response.data;
    },
};

// Compliance API
export const complianceApi = {
    list: async (pursuitId: number) => {
        const response = await apiClient.get(`/compliance/pursuit/${pursuitId}`);
        return response.data;
    },
    create: async (pursuitId: number, items: any[]) => {
        const response = await apiClient.post(`/compliance/pursuit/${pursuitId}`, items);
        return response.data;
    },
    update: async (id: number, data: any) => {
        const response = await apiClient.patch(`/compliance/${id}`, data);
        return response.data;
    },
    delete: async (id: number) => {
        await apiClient.delete(`/compliance/${id}`);
    },
};

// Subscriptions API
export const subscriptionsApi = {
    list: async () => {
        const response = await apiClient.get('/subscriptions');
        return response.data;
    },
    subscribe: async (agencyId: number) => {
        const response = await apiClient.post(`/subscriptions/${agencyId}`);
        return response.data;
    },
    unsubscribe: async (agencyId: number) => {
        await apiClient.delete(`/subscriptions/${agencyId}`);
    },
};

// Contractor Profile API
export const contractorProfileApi = {
    getProfile: async () => {
        const response = await apiClient.get('/contractor-profile');
        return response.data;
    },
    updateProfile: async (data: any) => {
        const response = await apiClient.put('/contractor-profile', data);
        return response.data;
    },
    // Key Personnel
    getPersonnel: async () => {
        const response = await apiClient.get('/contractor-profile/personnel');
        return response.data;
    },
    createPersonnel: async (data: any) => {
        const response = await apiClient.post('/contractor-profile/personnel', data);
        return response.data;
    },
    updatePersonnel: async (id: number, data: any) => {
        const response = await apiClient.put(`/contractor-profile/personnel/${id}`, data);
        return response.data;
    },
    deletePersonnel: async (id: number) => {
        await apiClient.delete(`/contractor-profile/personnel/${id}`);
    },
    // Past Performance
    getPastPerformance: async () => {
        const response = await apiClient.get('/contractor-profile/past-performance');
        return response.data;
    },
    createPastPerformance: async (data: any) => {
        const response = await apiClient.post('/contractor-profile/past-performance', data);
        return response.data;
    },
    updatePastPerformance: async (id: number, data: any) => {
        const response = await apiClient.put(`/contractor-profile/past-performance/${id}`, data);
        return response.data;
    },
    deletePastPerformance: async (id: number) => {
        await apiClient.delete(`/contractor-profile/past-performance/${id}`);
    },
    // Certifications
    getCertifications: async () => {
        const response = await apiClient.get('/contractor-profile/certifications');
        return response.data;
    },
    createCertification: async (data: any) => {
        const response = await apiClient.post('/contractor-profile/certifications', data);
        return response.data;
    },
    updateCertification: async (id: number, data: any) => {
        const response = await apiClient.put(`/contractor-profile/certifications/${id}`, data);
        return response.data;
    },
    deleteCertification: async (id: number) => {
        await apiClient.delete(`/contractor-profile/certifications/${id}`);
    },
    // Insurance
    getInsurance: async () => {
        const response = await apiClient.get('/contractor-profile/insurance');
        return response.data;
    },
    createInsurance: async (data: any) => {
        const response = await apiClient.post('/contractor-profile/insurance', data);
        return response.data;
    },
    updateInsurance: async (id: number, data: any) => {
        const response = await apiClient.put(`/contractor-profile/insurance/${id}`, data);
        return response.data;
    },
    deleteInsurance: async (id: number) => {
        await apiClient.delete(`/contractor-profile/insurance/${id}`);
    },
};

// Submittals API
export const submittalsApi = {
    // Document Templates
    listTemplates: async (params?: { agency?: string }) => {
        const response = await apiClient.get('/submittals/templates', { params });
        return response.data;
    },
    createTemplate: async (data: any) => {
        const response = await apiClient.post('/submittals/templates', data);
        return response.data;
    },
    deleteTemplate: async (id: number) => {
        await apiClient.delete(`/submittals/templates/${id}`);
    },
    // Submittal Slots
    listSlots: async (pursuitId: number) => {
        const response = await apiClient.get(`/submittals/pursuits/${pursuitId}/slots`);
        return response.data;
    },
    createSlot: async (pursuitId: number, data: any) => {
        const response = await apiClient.post(`/submittals/pursuits/${pursuitId}/slots`, data);
        return response.data;
    },
    updateSlot: async (id: number, data: any) => {
        const response = await apiClient.patch(`/submittals/slots/${id}`, data);
        return response.data;
    },
    deleteSlot: async (id: number) => {
        await apiClient.delete(`/submittals/slots/${id}`);
    },
    triggerAutofill: async (id: number) => {
        const response = await apiClient.post(`/submittals/slots/${id}/autofill`);
        return response.data;
    },
    // Final Submittals
    listFinalSubmittals: async (pursuitId: number) => {
        const response = await apiClient.get(`/submittals/pursuits/${pursuitId}/final`);
        return response.data;
    },
    createFinalSubmittal: async (pursuitId: number, data: any) => {
        const response = await apiClient.post(`/submittals/pursuits/${pursuitId}/final`, data);
        return response.data;
    },
    updateFinalSubmittal: async (id: number, data: any) => {
        const response = await apiClient.patch(`/submittals/final/${id}`, data);
        return response.data;
    },
    deleteFinalSubmittal: async (id: number) => {
        await apiClient.delete(`/submittals/final/${id}`);
    }
};

export default apiClient;
